/**
 * "Change road provider": the priced carriers of one trucking leg, in a side
 * panel. Picking a card applies at once; booking a road leg is not built, so
 * the Book button is shown disabled.
 */
import React, { useEffect, useState } from 'react';
import { Tooltip } from 'antd';

import type { AccessLeg, TruckingLeg } from '../../../apis/lanePlanner';
import { CommonDrawer } from '../../../components';
import { formatMoney } from './quoteView';
import styles from './styles.module.css';
import { arrivalOf, pickedOf, pickupDayOf, TruckingFit } from './truckingView';

interface TruckingProviderDrawerProps {
  open: boolean;
  leg: TruckingLeg;
  entry?: AccessLeg;
  /** The leg is being quoted again; `entry` then holds the options from before. */
  refreshing?: boolean;
  shipDate?: string;
  /** The day the quote it was opened from arrives, for a delivery leg's pickup day. */
  arrivalDay?: string | null;
  /** Which carriers work with the quote's dates; the others are not offered. */
  fits?: TruckingFit;
  /** The picked option; the cheapest when none is picked. */
  selectedId?: string;
  onSelect: (id: string) => void;
  onClose: () => void;
}

const TruckingProviderDrawer: React.FC<TruckingProviderDrawerProps> = ({
  open,
  leg,
  entry,
  refreshing = false,
  shipDate,
  arrivalDay,
  fits,
  selectedId,
  onSelect,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  // each opening starts with the whole list
  useEffect(() => {
    if (open) setQuery('');
  }, [open, leg]);
  const options = (entry?.options ?? []).filter((option) =>
    fits ? fits(option) : true
  );
  const current = entry && pickedOf(entry, selectedId, fits);
  const needle = query.trim().toLowerCase();
  const shown = options.filter((option) =>
    `${option.carrier ?? ''} ${option.service_level ?? ''}`
      .toLowerCase()
      .includes(needle)
  );
  const pickup = pickupDayOf(leg, shipDate, arrivalDay);

  return (
    <CommonDrawer open={open} onClose={onClose} onBack={onClose} width={552}>
      <div className={styles.provider}>
        <div className={styles.providerBody}>
          <h3 className={styles.providerTitle}>Change road provider</h3>
          <div className={styles.providerSub}>
            {entry ? `${entry.from} → ${entry.to}` : ''}
            {pickup ? ` · Pickup ${pickup.format('M/D')}` : ''}
          </div>
          <input
            type='search'
            className={styles.providerSearch}
            placeholder='Search carriers'
            aria-label='Search carriers'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div
            className={styles.providerList}
            role='radiogroup'
            aria-label='Road carriers'
          >
            {refreshing && <div className={styles.providerSub}>Updating…</div>}
            {!refreshing && shown.length === 0 && (
              <div className={styles.providerSub}>No carriers match</div>
            )}
            {shown.map((option) => {
              const arrival = arrivalOf(pickup, option.transit_minutes);
              return (
                <label
                  key={option.id}
                  className={`${styles.providerCard} ${
                    option === current ? styles.providerCardOn : ''
                  }`}
                >
                  <input
                    type='radio'
                    name='trucking-carrier'
                    className={styles.srOnly}
                    checked={option === current}
                    onChange={() => onSelect(option.id)}
                  />
                  <span className={styles.providerName}>
                    {option.carrier ?? 'Carrier'}
                    {option.service_level && (
                      <span className={styles.providerLevel}>
                        {' · '}
                        {option.service_level}
                      </span>
                    )}
                  </span>
                  <span className={styles.providerPrice}>
                    <span>{formatMoney(option.price, option.currency)}</span>
                    <span className={styles.providerCurrency}>
                      {option.currency ?? 'USD'}
                    </span>
                  </span>
                  {arrival && (
                    <span className={styles.providerTag}>
                      Arrives ~{arrival.format('ddd, MMM D, YYYY')} by end of
                      day
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
        <div className={styles.providerFooter}>
          <Tooltip title="Booking road legs isn't available yet">
            <span className={styles.providerBook} tabIndex={0}>
              <button
                type='button'
                className={styles.providerBookButton}
                disabled
              >
                Book &amp; Generate Label
              </button>
            </span>
          </Tooltip>
          <button
            type='button'
            className={styles.providerClose}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </CommonDrawer>
  );
};

export default TruckingProviderDrawer;
